import { Button, cn } from '@crm/ui';
import { BookOpen, Menu, Moon, Sun, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { FullscreenButton } from '@/components';
import { UserMenu } from '@/modules/auth/components';
import { AUTH_ROUTES } from '@/modules/auth/constants';
import { useSession } from '@/modules/auth/hooks';
import { NotificationBell, NotificationToasts, useUnreadCount } from '@/modules/notifications';
import { useTheme } from '../providers';
import { FeatureSearch } from './feature-search';
import { MobileNav } from './mobile-nav';
import { type NavSection, useNavSections, useOpenSection } from './navigation';
import { SectionFlyout, Sidebar } from './sidebar';
import { useLayoutStore } from './use-layout-store';

/** Casca da area autenticada: cabecalho fixo + navegacao lateral + rota. */
/**
 * O respiro do conteudo em toda tela - inclusive nas de largura cheia, como a
 * agenda. Mude aqui e o sistema inteiro acompanha; e o que mantem o titulo na
 * mesma altura em todas as paginas.
 */
const PAGE_PADDING = 'px-3 py-4 sm:px-4 lg:px-10 lg:py-10';

/**
 * Altura da barra do topo. Fica em um lugar so porque a coluna lateral, o
 * submenu e o fundo escuro descontam esse valor da tela - com um numero
 * chutado, sobra faixa branca embaixo da barra ao rolar.
 */
const HEADER_HEIGHT = '4rem';

/**
 * Fundo das telas de operacao (agenda e fila do dia). As duas usam o mesmo:
 * era o cinza so na agenda que fazia uma parecer de outro sistema. Trocar
 * aqui muda as duas.
 */
const OPERATION_BG = 'bg-muted/40';

/**
 * Fundo da area de conteudo: os dois tons vem do tema (`--content` e
 * `--content-strong`), entao a intensidade se ajusta la, num lugar so.
 */
const CONTENT_BG = 'bg-content max-sm:bg-content-strong';

/**
 * Separacao entre a barra do topo e o conteudo, em um lugar so. A regra padrao
 * vale em toda tela: a barra precisa se destacar do que rola por baixo dela.
 */
const HEADER_EDGE =
  'border-b border-border shadow-[0_2px_6px_-2px_rgba(15,23,42,0.18)] dark:shadow-[0_2px_8px_-2px_rgba(0,0,0,0.9)]';

/**
 * Excecao das telas de operacao **no celular**: ali a tabpage encosta na barra
 * e ja faz a separacao - uma segunda linha forte viraria borda dupla. Do
 * tablet para cima nao existe tabpage, entao vale a regra padrao.
 */
const HEADER_EDGE_OPERATION =
  'max-sm:border-black/[0.02] max-sm:shadow-none max-sm:dark:border-white/[0.03]';

/**
 * Ate onde o conteudo cresce. As telas comuns leem melhor estreitas; as de
 * operacao aproveitam a largura, mas nao ate o infinito - em um monitor
 * grande o cartao viraria uma faixa.
 *
 * O teto sobe junto com a tela: parado em 1152px, um Full HD deixava um terco
 * da largura vazia e um 4K, dois tercos. Cada degrau so entra quando a tela
 * comporta o teto novo - senao a coluna encolheria em vez de crescer.
 *
 * O `mx-auto` centraliza no espaco depois da barra de menus, e nao na tela.
 * E de proposito: com um contrapeso do outro lado o desenho fica centrado na
 * geometria, mas a barra e massa e o contrapeso e vazio - sobrava sempre uma
 * barra a mais de branco na direita.
 */
const CONTENT_WIDTH =
  'mx-auto w-full max-w-6xl min-[1600px]:max-w-[1440px] min-[1920px]:max-w-[1760px]';
const OPERATION_WIDTH = 'mx-auto w-full max-w-[1600px] min-[2200px]:max-w-[2000px]';

export function AppLayout() {
  const naoLidas = useUnreadCount();
  const temAviso = (naoLidas.data?.total ?? 0) > 0;

  const { user } = useSession();
  const { theme, toggleTheme } = useTheme();
  const isNavOpen = useLayoutStore((state) => state.isMobileNavOpen);
  const setIsNavOpen = useLayoutStore((state) => state.setMobileNavOpen);
  const isFullWidth = useLayoutStore((state) => state.isFullWidth);
  const isPlainBackground = useLayoutStore((state) => state.isPlainBackground);
  const { openSection, closeSection, closeAfterNavigate } = useOpenSection();
  const sections = useNavSections();
  const navRef = useRef<HTMLElement>(null);

  /**
   * O painel guarda a ultima secao aberta enquanto some da tela: sem isso o
   * conteudo desaparece antes da animacao terminar.
   */
  const [flyoutSection, setFlyoutSection] = useState<NavSection | undefined>();

  useEffect(() => {
    if (!openSection) return;

    setFlyoutSection(sections.find((section) => section.title === openSection));
  }, [openSection, sections]);

  /**
   * Clique fora do menu fecha o submenu e devolve a barra estreita. Sem a
   * checagem de `navRef.current`, um ref ainda vazio faria todo clique passar
   * por "fora" - inclusive o do proprio submenu, que fechava o menu antes de
   * o link navegar.
   */
  useEffect(() => {
    if (!openSection) return;

    const onPointerDown = (event: MouseEvent) => {
      // A barra e o painel sobreposto sao a mesma navegacao, ainda que em
      // pontos diferentes da arvore - por isso a marca no elemento.
      const alvo = (event.target as HTMLElement).closest('[data-nav-root]');
      if (!alvo) closeSection();
    };

    document.addEventListener('mousedown', onPointerDown);

    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [openSection, closeSection]);

  return (
    <div className="min-h-screen bg-background">
      {/* Um degrade bem curto separa a barra do conteudo que passa por baixo
          dela - sem sombra, que ja pesava demais no topo. */}
      {/* Acima do submenu e do fundo escuro: a barra do topo nunca e coberta.
          O fundo e solido e a borda ganha sombra: sem isso ela se perdia no
          corpo da pagina. */}
      <header
        style={{ height: HEADER_HEIGHT }}
        className={`sticky top-0 z-40 flex items-center bg-background lg:static ${HEADER_EDGE} ${
          isFullWidth ? HEADER_EDGE_OPERATION : ''
        }`}
      >
        <div className="flex w-full items-center justify-between gap-4 px-4">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              // So o celular abre e fecha a navegacao: no desktop a barra e
              // fixa, sempre com os nomes das secoes a vista.
              onClick={() => setIsNavOpen(!isNavOpen)}
              aria-label={isNavOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={isNavOpen}
              className="h-11 w-11 lg:hidden [&_svg]:size-6"
            >
              {isNavOpen ? <X /> : <Menu />}
            </Button>

            <Link to={AUTH_ROUTES.DASHBOARD} className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground">
                C
              </div>
              <span className="text-lg font-semibold tracking-tight">CRM</span>
            </Link>
          </div>

          <div className="flex items-center gap-2">
            <FeatureSearch />

            {/* O sino fica antes dos atalhos do aplicativo: e o unico ali
                que muda sozinho e pede atencao. */}
            <NotificationBell />

            {/* Tela cheia mora aqui, nao em cada pagina: a acao e do
                aplicativo inteiro e vale em qualquer tela. */}
            <FullscreenButton compact />

            <Button
              variant="ghost"
              size="icon"
              asChild
              title="Documentacao da API"
              className="hidden h-12 w-12 sm:inline-flex [&_svg]:size-7"
            >
              <a href="/docs" target="_blank" rel="noreferrer" aria-label="Documentacao da API">
                <BookOpen />
              </a>
            </Button>

            {/* Atalho do tema so no desktop: no celular ele vive em
                Preferencias, dentro do menu da conta. */}
            <Button
              variant="ghost"
              size="icon"
              className="hidden h-12 w-12 sm:inline-flex [&_svg]:size-7"
              onClick={toggleTheme}
              title="Alternar tema"
              aria-label="Alternar tema"
            >
              {theme === 'dark' ? <Sun /> : <Moon />}
            </Button>

            {/* No celular a conta esta na gaveta; aqui ela so ocuparia espaco. */}
            {user ? (
              <span className="hidden sm:block">
                <UserMenu user={user} />
              </span>
            ) : null}
          </div>
        </div>
      </header>

      {/* Altura minima da tela cheia: sem isso a barra de menus termina onde o
          conteudo termina e parece cortada nas paginas curtas. */}
      <div className="flex" style={{ minHeight: `calc(100vh - ${HEADER_HEIGHT})` }}>
        {/* Navegacao fixa no desktop */}
        {/* Recolhida vira uma trilha de icones; a agenda entra assim. */}
        {/* A largura da barra nao muda mais ao abrir o submenu: era o
            reflow da tela inteira que dava a sensacao de travamento. */}
        <aside
          ref={navRef}
          data-nav-root
          // Prateado: um passo mais frio e mais escuro que o cinza neutro, o
          // que separa a barra do conteudo sem precisar de sombra pesada.
          className="hidden w-24 shrink-0 border-r border-border bg-[#e6e9ef] shadow-[1px_0_0_0_rgba(15,23,42,0.06)] lg:block dark:bg-[#191b20]"
        >
          <Sidebar inlineSubmenu={false} />
        </aside>

        {/* Fundo de modal sobre o conteudo - a barra de menus continua a
            vista e clicavel, senao trocar de secao exigiria fechar antes. */}
        <button
          type="button"
          tabIndex={openSection ? 0 : -1}
          aria-label="Fechar submenu"
          onClick={closeSection}
          style={{ left: '6rem', top: HEADER_HEIGHT }}
          className={`fixed bottom-0 right-0 z-20 hidden bg-black/40 transition-opacity duration-200 ease-out motion-reduce:transition-none lg:block ${
            openSection ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        />

        <SectionFlyout
          section={flyoutSection}
          isOpen={Boolean(openSection)}
          onNavigate={(to) => closeAfterNavigate(to)}
          style={{
            left: '6rem',
            top: HEADER_HEIGHT,
            height: `calc(100vh - ${HEADER_HEIGHT})`,
          }}
        />

        {/* Navegacao do celular: a lista das secoes ocupa a largura toda, sob
            a barra do topo - e por ela, no X, que o menu fecha. */}
        {isNavOpen ? (
          <div
            style={{ top: HEADER_HEIGHT }}
            className="fixed inset-x-0 bottom-0 z-30 overflow-y-auto bg-background lg:hidden"
          >
            <MobileNav onNavigate={() => setIsNavOpen(false)} />
          </div>
        ) : null}

        <main
          // O PDF e a impressao saem daqui: e o conteudo da tela, sem casca.
          data-print-root
          className={cn(
            'min-w-0 flex-1 overflow-x-clip',
            PAGE_PADDING,
            CONTENT_BG,
            isFullWidth ? OPERATION_BG : '',
            isPlainBackground ? 'bg-card' : '',
            // O aviso de notificacao mora no rodape: sem esta folga ele
            // cobriria o fim da pagina no celular, onde nao sobra canto.
            temAviso ? 'pb-28 sm:pb-0' : '',
          )}
        >
          <div className={isFullWidth ? OPERATION_WIDTH : CONTENT_WIDTH}>
            <Outlet />
          </div>
        </main>
      </div>

      {/* Fica fora do conteudo: o aviso vale em qualquer tela do sistema. */}
      <NotificationToasts />
    </div>
  );
}
